import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { UserButton, useUser } from '@clerk/clerk-react';
import {
  Home, Users, Settings, LayoutDashboard, MessageSquare, UserCheck, Ticket,
  Calendar, Award, FileText, ShoppingBag, Share2, BarChart3, Bell, Menu, Trophy, HomeIcon, Building2, ScrollText, Shield,
  Banknote,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { FeedbackWidget } from './FeedbackWidget';
import { usePermissions } from '@/hooks/usePermissions';

// `requires` is the permission key the caller needs to even see this nav
// entry. Items with no requirement are visible to anyone signed in.
interface NavItem { label: string; icon: typeof Users; to: string; end: boolean; requires?: string }

const NAV: NavItem[] = [
  { label: 'Overview',      icon: LayoutDashboard, to: '/dashboard',               end: true  },
  { label: 'Members',       icon: Users,           to: '/dashboard/members',       end: false, requires: 'members:read' },
  { label: 'Households',    icon: HomeIcon,        to: '/dashboard/households',    end: false, requires: 'households:read' },
  { label: 'Check-ins',     icon: UserCheck,       to: '/dashboard/checkins',      end: false, requires: 'checkins:read' },
  { label: 'Cash payments', icon: Banknote,        to: '/dashboard/cash',          end: false, requires: 'payments:read' },
  { label: 'Day passes',    icon: Ticket,          to: '/dashboard/day-passes',    end: false, requires: 'daypasses:read' },
  { label: 'Schedule',      icon: Calendar,        to: '/dashboard/schedule',      end: false, requires: 'schedule:read' },
  { label: 'Events',        icon: Trophy,          to: '/dashboard/events',        end: false, requires: 'events:read' },
  { label: 'Coaches',       icon: Award,           to: '/dashboard/coaches',       end: false, requires: 'coaches:read' },
  { label: 'Waivers',       icon: FileText,        to: '/dashboard/waivers',       end: false, requires: 'waivers:read' },
  { label: 'Store',         icon: ShoppingBag,     to: '/dashboard/store',         end: false, requires: 'store:read' },
  { label: 'Social',        icon: Share2,          to: '/dashboard/social',        end: false, requires: 'social:read' },
  { label: 'Reports',       icon: BarChart3,       to: '/dashboard/reports',       end: false, requires: 'reports:read' },
  { label: 'Notifications', icon: Bell,            to: '/dashboard/notifications', end: false, requires: 'notifications:read' },
  { label: 'Feedback',      icon: MessageSquare,   to: '/dashboard/feedback',      end: false, requires: 'feedback:read' },
  { label: 'Orgs',          icon: Building2,       to: '/dashboard/orgs',          end: false, requires: 'platform:orgs:read' },
  { label: 'Team',          icon: Users,           to: '/dashboard/team',          end: false, requires: 'org:team:read' },
  { label: 'Audit log',     icon: ScrollText,      to: '/dashboard/audit-log',     end: false, requires: 'audit:read' },
  { label: 'Data requests', icon: Shield,          to: '/dashboard/data-requests', end: false, requires: 'data_requests:manage' },
  { label: 'Settings',      icon: Settings,        to: '/dashboard/settings',      end: false },
];

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const { has, loading } = usePermissions();
  // While permissions are loading, show only items with no permission gate so
  // we don't briefly hide everything before the call returns.
  return (
    <>
      {NAV.filter((item) => !item.requires || loading || has(item.requires)).map((item) => (
        <NavLink
          key={item.label}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ' +
            (isActive
              ? 'bg-accent text-accent-foreground font-medium'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground')
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
    </>
  );
}

function OrgSwitcher() {
  const { orgs, currentOrgId, setCurrentOrgId, data } = usePermissions();
  if (!data) return null;
  if (orgs.length <= 1) {
    const single = orgs[0];
    if (!single) return null;
    return <div className="px-3 py-2 text-xs text-muted-foreground truncate">{single.name}</div>;
  }
  return (
    <select
      value={currentOrgId ?? ''}
      onChange={(e) => setCurrentOrgId(e.target.value || null)}
      className="w-full text-xs border rounded px-2 py-1.5 bg-background"
    >
      {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  );
}

export function DashboardLayout() {
  const { user } = useUser();
  const firstName = user?.firstName ?? user?.username ?? 'there';
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the drawer when the user navigates (defensive — onClick on each link
  // already does this, but covers programmatic navigation too).
  void location;

  const currentLabel = NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))?.label;

  return (
    <div className="min-h-screen flex bg-muted/40">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-60 flex-col border-r bg-background">
        <Link to="/dashboard" className="h-16 flex items-center px-6 border-b">
          <Home className="h-5 w-5 mr-2" />
          <span className="font-semibold">membersyncpro</span>
        </Link>
        <div className="px-3 pt-3"><OrgSwitcher /></div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavItems />
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — hamburger + title on mobile, welcome on desktop */}
        <header className="h-16 border-b bg-background px-4 md:px-6 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  className="inline-flex items-center justify-center rounded-md h-9 w-9 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 flex flex-col gap-0">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <div className="h-16 flex items-center px-6 border-b">
                  <Home className="h-5 w-5 mr-2" />
                  <span className="font-semibold">membersyncpro</span>
                </div>
                <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                  <NavItems onNavigate={() => setOpen(false)} />
                </nav>
              </SheetContent>
            </Sheet>
            <h1 className="text-base font-semibold truncate">
              {currentLabel ?? 'membersyncpro'}
            </h1>
          </div>
          <div className="hidden md:block text-sm text-muted-foreground">
            Welcome back, {firstName}.
          </div>
          <UserButton afterSignOutUrl="/sign-in" />
        </header>

        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      <FeedbackWidget />
    </div>
  );
}
